'use client';
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ElowenApiError, elowenClient } from "../elowenClient";
import { QUERY_KEYS } from "../queries";
import type { MemoryCreate, MemoryPatch, EmbeddingSettingsPatch, MemoryCategoryCreate, MemoryCategoryPatch, CategorizationSettingsPatch, MemoryMaintenanceState, MemoryRecategorizeMode, MemoryDreamingSettingsSnapshot, MemoryDreamingSettingsPatch } from "../types";

/** Create a memory (source 'user'). Refreshes the list and the audit feed. */
export function useCreateMemory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MemoryCreate) => elowenClient.createMemory(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: QUERY_KEYS.memories }); qc.invalidateQueries({ queryKey: ['memory-events'] }); },
  });
}

/** Patch a memory (body/kind/importance/status). Refreshes the list, that memory's detail
 *  and audit trail, and the whole-user event feed. */
export function useUpdateMemory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: number; patch: MemoryPatch }) => elowenClient.updateMemory(v.id, v.patch),
    onSuccess: (_r, v) => {
      qc.invalidateQueries({ queryKey: QUERY_KEYS.memories });
      qc.invalidateQueries({ queryKey: ['memory', v.id] });
      qc.invalidateQueries({ queryKey: ['memory-events'] });
    },
  });
}

/** Assign (or clear) a memory's category — a separate audited write (PUT /memory/:id/category), NOT a
 *  PATCH field. Refreshes the list, that memory's detail and the audit feed. */
export function useSetMemoryCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: number; categoryId: number | null }) => elowenClient.setMemoryCategory(v.id, v.categoryId),
    onSuccess: (_r, v) => {
      qc.invalidateQueries({ queryKey: QUERY_KEYS.memories });
      qc.invalidateQueries({ queryKey: ['memory', v.id] });
      qc.invalidateQueries({ queryKey: ['memory-events'] });
    },
  });
}

/** Soft-delete a memory. Refreshes the list, that memory's detail and the audit feed. */
export function useDeleteMemory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => elowenClient.deleteMemory(id),
    onSuccess: (_r, id) => {
      qc.invalidateQueries({ queryKey: QUERY_KEYS.memories });
      qc.invalidateQueries({ queryKey: ['memory', id] });
      qc.invalidateQueries({ queryKey: ['memory-events'] });
    },
  });
}

/** Restore a soft-deleted memory back to active. */
export function useRestoreMemory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => elowenClient.restoreMemory(id),
    onSuccess: (_r, id) => {
      qc.invalidateQueries({ queryKey: QUERY_KEYS.memories });
      qc.invalidateQueries({ queryKey: ['memory', id] });
      qc.invalidateQueries({ queryKey: ['memory-events'] });
    },
  });
}

/** Hard-delete many owned memories in one call — irreversible. Refreshes the list and audit feed. */
export function usePurgeMemories() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) => elowenClient.purgeMemories(ids),
    onSuccess: () => { qc.invalidateQueries({ queryKey: QUERY_KEYS.memories }); qc.invalidateQueries({ queryKey: ['memory-events'] }); },
  });
}

/** Empty the trash — hard-delete ALL of the caller's soft-deleted memories. Refreshes list and audit feed. */
export function useEmptyTrash() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => elowenClient.emptyTrash(),
    onSuccess: () => { qc.invalidateQueries({ queryKey: QUERY_KEYS.memories }); qc.invalidateQueries({ queryKey: ['memory-events'] }); },
  });
}

/** Merge several memories into a new one (sources soft-deleted). Refreshes the list and audit feed. */
export function useMergeMemories() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { ids: number[]; body: string; kind?: string }) => elowenClient.mergeMemories(v.ids, v.body, v.kind),
    onSuccess: () => { qc.invalidateQueries({ queryKey: QUERY_KEYS.memories }); qc.invalidateQueries({ queryKey: ['memory-events'] }); },
  });
}

/** Acknowledge a settled receipt without affecting a newer run. */
export function useAcknowledgeMemoryMaintenance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => elowenClient.acknowledgeMemoryMaintenance(id),
    onSuccess: (state) => qc.setQueryData(QUERY_KEYS.memoryMaintenance, state),
  });
}

/** Start owner-scoped background reindexing and refresh its progress slot. */
export function useStartMemoryReindex() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => elowenClient.startMemoryReindex(),
    onSuccess: (job) => {
      qc.setQueryData(QUERY_KEYS.memoryMaintenance, (current: MemoryMaintenanceState | undefined) => current ? { ...current, reindex: job } : current);
      void qc.invalidateQueries({ queryKey: QUERY_KEYS.memoryMaintenance });
    },
  });
}

/** Start owner-scoped background recategorization and refresh its progress slot. */
export function useStartMemoryRecategorize() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mode: MemoryRecategorizeMode) => elowenClient.startMemoryRecategorize(mode),
    onSuccess: (job) => {
      qc.setQueryData(QUERY_KEYS.memoryMaintenance, (current: MemoryMaintenanceState | undefined) => current ? { ...current, recategorize: job } : current);
      void qc.invalidateQueries({ queryKey: QUERY_KEYS.memoryMaintenance });
    },
  });
}

/** Save the account's automatic memory-maintenance preference. Same revision pattern as the other
 *  personal-settings saves: the cached `revision` rides along, the saved answer replaces the cache, and a
 *  409 means another tab got there first — the daemon's `current` replaces the cache so an explicit
 *  Reload starts from the state that actually won. */
export function useSaveMyMemoryDreaming() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Omit<MemoryDreamingSettingsPatch, 'expectedRevision'>) => {
      const current = qc.getQueryData<MemoryDreamingSettingsSnapshot>(QUERY_KEYS.myMemoryDreaming);
      if (!current) throw new Error('Memory maintenance settings have not loaded');
      return elowenClient.saveMyMemoryDreaming({ ...patch, expectedRevision: current.revision });
    },
    onSuccess: (saved) => {
      qc.setQueryData(QUERY_KEYS.myMemoryDreaming, saved);
      void qc.invalidateQueries({ queryKey: QUERY_KEYS.memoryDreaming });
    },
    onError: (error) => {
      if (error instanceof ElowenApiError && error.status === 409) {
        const current = error.details?.current;
        if (current) qc.setQueryData(QUERY_KEYS.myMemoryDreaming, current);
      }
    },
  });
}

/** Start a maintenance pass now. The overview is re-read rather than patched: the daemon answers the run
 *  it started (or the one already running), and the status carries both that row and the fresh state. */
export function useStartMemoryDreaming() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => elowenClient.startMemoryDreaming(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: QUERY_KEYS.memoryDreaming }); },
  });
}

/** Stop a running pass. What it already wrote stays; only future work is abandoned. */
export function useCancelMemoryDreaming() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => elowenClient.cancelMemoryDreaming(id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: QUERY_KEYS.memoryDreaming }); },
  });
}

/** Remove settled history and refresh the same prefix used by the account card and operation dock. */
export function useDeleteMemoryDreaming() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => elowenClient.deleteMemoryDreaming(id),
    onSuccess: async (_result, id) => {
      qc.removeQueries({ queryKey: [...QUERY_KEYS.memoryDreaming, 'run', id], exact: true });
      await qc.invalidateQueries({ queryKey: QUERY_KEYS.memoryDreaming });
    },
  });
}

/** Save the workspace embedding provider settings (admin). Refreshes the settings query. */
export function useSaveEmbeddingSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: EmbeddingSettingsPatch) => elowenClient.saveEmbeddingSettings(patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEYS.embeddingSettings }),
  });
}

/** Create a memory category. Refreshes the category list and the memory list (badges/filters). */
export function useCreateMemoryCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MemoryCategoryCreate) => elowenClient.createMemoryCategory(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: QUERY_KEYS.memoryCategories }); qc.invalidateQueries({ queryKey: QUERY_KEYS.memories }); },
  });
}

/** Patch a memory category (name/description/color). Refreshes the category list and the memory list. */
export function useUpdateMemoryCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { cid: number; patch: MemoryCategoryPatch }) => elowenClient.updateMemoryCategory(v.cid, v.patch),
    onSuccess: () => { qc.invalidateQueries({ queryKey: QUERY_KEYS.memoryCategories }); qc.invalidateQueries({ queryKey: QUERY_KEYS.memories }); },
  });
}

/** Delete a memory category (clears category_id on referencing memories). Refreshes categories and memories. */
export function useDeleteMemoryCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cid: number) => elowenClient.deleteMemoryCategory(cid),
    onSuccess: () => { qc.invalidateQueries({ queryKey: QUERY_KEYS.memoryCategories }); qc.invalidateQueries({ queryKey: QUERY_KEYS.memories }); },
  });
}

/** Save the workspace categorization provider settings (admin). Refreshes the settings query. */
export function useSaveCategorizationSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: CategorizationSettingsPatch) => elowenClient.saveCategorizationSettings(patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEYS.categorizationSettings }),
  });
}
